import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    addBrandsSelection,
    getSelectedBrands,
    getBrands,
    getBrandState,
    clearFilters,
    updateCheckboxes,
    getNextPageBrands,
    getCheckboxState,
    searchBrands,
    setCheckboxes,
    BrandState,
    getCountries,
} from '@emi/shared/state/filters';
import { CheckboxTreeNode, Brand, SubBrand, Page } from '@emi/shared/models';
import { Paging, TriStateCheckboxesFlow } from '@emi/shared/constants';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-brands',
    templateUrl: './filter-brands.component.html',
    styleUrls: ['./filter-brands.component.scss'],
})
export class FilterBrandsComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;

    private brandsSubscription: Subscription;
    private stateSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    public brandsTree: Array<CheckboxTreeNode> = [];
    public selectedBrands: Array<Brand> = [];
    public stateBrands: Array<Brand> = [];
    public loading: boolean;
    public triStateFlow: number = TriStateCheckboxesFlow.ALTERNATIVE_CHECKBOXES;
    public showError = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public errorMessage = '';
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    private nextPageId: string | undefined;
    public hasSearchedByText = false;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<BrandState>, private changeDetector : ChangeDetectorRef) {}

    public onScrollDown(): void {
        if (this.nextPageId) {
            this.store.dispatch(getNextPageBrands());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.brandsSubscription = this.store.pipe(select(getSelectedBrands)).subscribe((brands) => {
            if (brands.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: brands.map((brand) => {
                                return this.completeBrands(brand);
                            }),
                        })
                    );
                }
                this.selectedBrands = brands;
                this.brandsTree = brands.map((brand) => {
                    return this.buildCheckboxTreeNode(brand);
                });

                const nextPage = new Page('', this.brandsTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getBrandState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.pages = [];
            if (this.selectedBrands.length === 0) {
                this.brandsTree = state.brands.map((brand) => {
                    return this.buildCheckboxTreeNode(brand);
                });
            }
            this.eventsSubject.next(this.brandsTree);
            while (this.brandsTree.length > 0) {
                const newPage = this.brandsTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }
            this.stateBrands = state.brands;
            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const brands: Array<Brand> = this.checkedArray
                .map((brand) => this.buildBrand(brand))
                .sort((a, b) => (a.brandName.toLowerCase() < b.brandName.toLowerCase() ? -1 : 1));
            this.store.dispatch(addBrandsSelection({ brands }));
            this.store.dispatch(getCountries());
            this.store.dispatch(clearFilters());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedBrands.map((brand) => {
                            return this.buildCheckboxTreeNode(brand, true);
                        }),
                    })
                );
                const currentSelectableSuppliers = this.selectedBrands.length > 0 ? this.selectedBrands : this.stateBrands;
                this.brandsTree = currentSelectableSuppliers.map((brand) => {
                    return this.buildCheckboxTreeNode(brand, this.selectedBrands.length > 0);
                });
                this.eventsSubject.next(this.brandsTree);
                while (this.brandsTree.length > 0) {
                    const newPage = this.brandsTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedBrands.length === 0 && this.stateBrands.length === 0) {
            this.store.dispatch(getBrands());
        }
    }

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode, page: Page[]): void {
        this.store.dispatch(updateCheckboxes({ checkbox: this.filterPage(page, selectedCheckbox) }));
    }

    public filterPage(pages: Page[], selected: CheckboxTreeNode): CheckboxTreeNode {
        let parent;
        if (selected.parentId) {
            for (const page of pages) {
                parent = page.value.find((node) => node.id === selected.parentId);
            }
        }

        if (parent && parent.children) {
            let children = [];
            children = parent.children.filter((children) => children.state === 'checked');
            return new CheckboxTreeNode(
                parent.id,
                parent.label,
                parent.state,
                parent.isExpanded,
                children.map(
                    (child) => new CheckboxTreeNode(child.id, child.label, child.state, child.isExpanded, child.children, child.parentId)
                )
            );
        } else {
            return selected;
        }
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(
            this.selectedBrands.length + this.selectedBrands.map((x) => x.subBrands?.length ?? 0).reduce((a, b) => a + b, 0) !==
                this.checkedArray.length + this.checkedArray.map((x) => x.children?.length ?? 0).reduce((a, b) => a + b, 0) &&
                this.checkedArray.length > 0
        );
    }

    public buildBrand(node: CheckboxTreeNode): Brand {
        if (!node.children || node.children.length === 0) {
            return new Brand(node.id, node.label, [], true);
        }

        const subBrands = [];
        for (const child of node.children) {
            subBrands.push(new SubBrand(child.id, child.label));
        }

        return new Brand(
            node.id,
            node.label,
            subBrands.sort((a, b) => (a.subBrandName.toLowerCase() < b.subBrandName.toLowerCase() ? -1 : 1)),
            node.state === 'checked'
        );
    }

    public buildCheckboxTreeNode(brand: Brand, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            if (!brand.subBrands || brand.subBrands.length === 0) {
                return new CheckboxTreeNode(brand.brandId, brand.brandName, 'checked');
            }

            const children = brand.subBrands.map(
                (subBrand) => new CheckboxTreeNode(subBrand.subBrandId, subBrand.subBrandName, 'checked', true, undefined, brand.brandId)
            );
            return new CheckboxTreeNode(
                brand.brandId,
                brand.brandName,
                brand.isParentBrandSelected ? 'checked' : 'indeterminate',
                true,
                children
            );
        }

        const isExpanded = this.selectedBrands.length > 0;
        const existingBrand = this.checkedArray.find((x) => x.id === brand.brandId);

        if (!brand.subBrands || brand.subBrands.length === 0) {
            return new CheckboxTreeNode(brand.brandId, brand.brandName, existingBrand ? 'checked' : 'unchecked');
        }

        if (!existingBrand) {
            const children = brand.subBrands.map(
                (subBrand) => new CheckboxTreeNode(subBrand.subBrandId, subBrand.subBrandName, 'unchecked', false, undefined, brand.brandId)
            );
            return new CheckboxTreeNode(
                brand.brandId,
                brand.brandName,
                brand.isParentBrandSelected ? 'checked' : 'unchecked',
                this.hasSearchedByText,
                children
            );
        } else {
            const children = brand.subBrands.map(
                (subBrand) =>
                    new CheckboxTreeNode(
                        subBrand.subBrandId,
                        subBrand.subBrandName,
                        existingBrand.children?.find((x) => x.id === subBrand.subBrandId)?.state ?? 'unchecked',
                        isExpanded,
                        undefined,
                        brand.brandId
                    )
            );
            return new CheckboxTreeNode(brand.brandId, brand.brandName, existingBrand.state, isExpanded, children, undefined);
        }
    }

    public completeBrands(brand: Brand): CheckboxTreeNode {
        if (!brand.subBrands || brand.subBrands.length === 0) {
            return new CheckboxTreeNode(brand.brandId, brand.brandName, 'checked', true, undefined, undefined);
        }
        const children = [];
        for (const subBrand of brand.subBrands) {
            children.push(new CheckboxTreeNode(subBrand.subBrandId, subBrand.subBrandName, 'checked', true, undefined, brand.brandId));
        }
        return new CheckboxTreeNode(
            brand.brandId,
            brand.brandName,
            brand.isParentBrandSelected ? 'checked' : 'indeterminate',
            true,
            children,
            undefined
        );
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.store.dispatch(searchBrands({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.store.dispatch(searchBrands({ searchText: '', delay: 0 }));
    }

    public checkForResults(): void {
        if (!this.loading && this.stateBrands.length == 0 && !this.hasSearchedByText && this.selectedBrands.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
            return;
        }
        if (!this.loading && this.stateBrands.length == 0 && this.hasSearchedByText && this.selectedBrands.length == 0) {
            this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
            return;
        }
        this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedBrands.length === 0) {
            this.store.dispatch(searchBrands({ searchText: '', delay: 0 }));
        }
        this.stateSubscription.unsubscribe();
        this.checkBoxSubscription.unsubscribe();
        this.brandsSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();

        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) { // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
